import { isEqualWith } from "lodash";
import {
  RowDataErrors,
  RowDataObject,
  JsonSchemaProperty,
  RowScopedErrorsState,
  JsonSchemaItems,
  ErrorValidationResult,
  RowDataReducerState,
  RowDataReducerAction,
  RowData,
  RowDataValueType,
  JsonSchema,
  NestedObjectErrorState,
} from "../../../types";
import { TRACK_RECORD_DATA_STATE_KEYS } from "../constants";
import {
  getFieldsByTabKey,
  getRowDataFieldValue,
  getSchemaSections,
  isNewRecord,
  isSetupRecordActionsRestricted,
} from "../helpers";
import { validateCoRelationRowDataInput } from "./coRelationFieldValidations";
import { validateRowDataInput } from "./fieldValidations";

export const validateAllFields = ({
  rowDataReducerState,
  dispatch,
  isForDialogFields,
  selectedTabSectionKey,
  selectedDashboardTabSectionKey,
}: {
  rowDataReducerState: RowDataReducerState;
  dispatch: React.Dispatch<RowDataReducerAction>;
  isForDialogFields: boolean | undefined;
  selectedTabSectionKey: string | undefined;
  selectedDashboardTabSectionKey: string | undefined;
}): ErrorValidationResult => {
  const sectionsToValidate = getSchemaSections(
    rowDataReducerState.schema,
    selectedDashboardTabSectionKey,
  );
  let hasErrors = false;
  let updatedRowDataErrors: RowDataErrors = {};

  sectionsToValidate.forEach(({ sectionValue }) => {
    const validation = validateSectionFields({
      sectionFields: sectionValue || [],
      rowDataReducerState,
      isForDialogFields,
      selectedTabSectionKey,
      dispatch,
    });

    if (validation.hasErrors) {
      hasErrors = true;
      updatedRowDataErrors = {
        ...updatedRowDataErrors,
        ...validation.updatedRowDataErrors,
      };
    }
  });

  return { hasErrors, updatedRowDataErrors };
};

export const validateSectionFields = ({
  sectionFields,
  rowDataReducerState,
  isForDialogFields,
  selectedTabSectionKey,
  dispatch,
}: {
  sectionFields: string[];
  rowDataReducerState: RowDataReducerState;
  isForDialogFields: boolean | undefined;
  selectedTabSectionKey: string | undefined;
  dispatch?: React.Dispatch<RowDataReducerAction>;
}): ErrorValidationResult => {
  const { schema, rowData, dialogRowData = {} } = rowDataReducerState;
  let hasErrors = false;
  const updatedRowDataErrors: RowDataErrors = {};

  sectionFields.forEach((propertyKey) => {
    const property = schema.properties[propertyKey];
    const fieldValue = getRowDataFieldValue({
      propertyKey,
      rowData,
      isDialogField: isForDialogFields,
      dialogRowData,
      selectedTabSectionKey,
    });

    if (!property || fieldValue === undefined) return;

    if (property.items?.type === "object") {
      const { hasArrayObjectErrors, updatedArrayObjectErrors } =
        validateCoRelationArrayObjects(
          propertyKey,
          fieldValue as RowDataObject[],
          property,
          rowDataReducerState,
          selectedTabSectionKey,
          dispatch,
        );

      if (hasArrayObjectErrors) {
        hasErrors = true;
        updatedRowDataErrors[propertyKey] = updatedArrayObjectErrors;
      }

      return;
    }

    if (property.properties) {
      let hasNestedObjectErrors = false;
      const updatedNestedObjectErrors: NestedObjectErrorState = {};

      Object.keys(property.properties).forEach((propertySubKey) => {
        const nestedFieldValue = (fieldValue as RowDataObject)?.[
          propertySubKey
        ];
        const nestedProperty = property.properties?.[
          propertySubKey
        ] as JsonSchemaProperty;

        if (nestedProperty && nestedFieldValue !== undefined) {
          const validationError = validateRowDataInput(
            propertySubKey,
            nestedProperty,
            nestedFieldValue,
            rowDataReducerState,
            dispatch,
          );

          if (validationError.error) {
            hasNestedObjectErrors = true;
            updatedNestedObjectErrors[propertySubKey] = validationError;
          }
        }
      });

      if (hasNestedObjectErrors) {
        hasErrors = true;
        updatedRowDataErrors[propertyKey] = updatedNestedObjectErrors;
      }
    }

    const validationError = validateRowDataInput(
      propertyKey,
      property,
      fieldValue,
      rowDataReducerState,
      dispatch,
    );

    if (validationError.error) {
      hasErrors = true;
      updatedRowDataErrors[propertyKey] = validationError;
    }
  });

  return { hasErrors, updatedRowDataErrors };
};

export const validateCoRelationArrayObjects = (
  propertyKey: string,
  propertyArray: RowDataObject[],
  property: JsonSchemaProperty,
  rowDataReducerState: RowDataReducerState,
  selectedTabSectionKey: string | undefined,
  dispatch?: React.Dispatch<RowDataReducerAction>,
): {
  hasArrayObjectErrors: boolean;
  updatedArrayObjectErrors: RowScopedErrorsState;
} => {
  let hasArrayObjectErrors = false;
  const updatedArrayObjectErrors: RowScopedErrorsState = {};

  propertyArray?.forEach((item, index) => {
    const { itemHasErrors, itemErrors } = validateSingleArrayObjectItem(
      index,
      item,
      propertyKey,
      property,
      rowDataReducerState,
      selectedTabSectionKey,
      dispatch,
    );

    if (itemHasErrors) {
      hasArrayObjectErrors = true;
      updatedArrayObjectErrors[index] = itemErrors;
    }
  });

  return { hasArrayObjectErrors, updatedArrayObjectErrors };
};

export const validateSingleArrayObjectItem = (
  index: number,
  item: RowDataObject,
  propertyKey: string,
  property: JsonSchemaProperty,
  rowDataReducerState: RowDataReducerState,
  selectedTabSectionKey: string | undefined,
  dispatch?: React.Dispatch<RowDataReducerAction>,
): {
  itemHasErrors: boolean;
  itemErrors: RowScopedErrorsState[number];
} => {
  let itemHasErrors = false;
  const itemErrors: RowScopedErrorsState[number] = {};

  Object.keys(item).forEach((propertySubKey) => {
    const fieldValue = item[propertySubKey];

    if (property && fieldValue !== undefined) {
      const validationError = validateCoRelationRowDataInput({
        propertyKey,
        propertySubKey,
        rowIndex: index,
        newFieldValue: fieldValue,
        items: property.items as JsonSchemaItems,
        selectedTabSectionKey,
        rowDataReducerState,
        dispatch,
      });

      if (validationError.error) {
        itemHasErrors = true;
        itemErrors[propertySubKey] = validationError;
      }
    }
  });

  return { itemHasErrors, itemErrors };
};

export const performRecordValidations = ({
  selectedTabSectionKey,
  selectedDashboardTabSectionKey,
  rowDataReducerState,
  dispatch,
}: {
  selectedTabSectionKey: string | undefined;
  selectedDashboardTabSectionKey: string | undefined;
  rowDataReducerState: RowDataReducerState;
  dispatch: React.Dispatch<RowDataReducerAction>;
}): ErrorValidationResult => {
  const { schema } = rowDataReducerState;
  const validationResult = selectedTabSectionKey
    ? validateSectionFields({
        sectionFields: getFieldsByTabKey(
          schema.sections,
          selectedTabSectionKey,
        ),
        rowDataReducerState,
        isForDialogFields: undefined,
        selectedTabSectionKey,
        dispatch,
      })
    : validateAllFields({
        rowDataReducerState,
        dispatch,
        isForDialogFields: undefined,
        selectedTabSectionKey: undefined,
        selectedDashboardTabSectionKey,
      });

  dispatch({
    type: "UPDATE_ROW_DATA_REDUCER_STATE",
    payload: { rowDataErrors: validationResult.updatedRowDataErrors },
  });

  return validationResult;
};

export const handleRecordChangesAndValidationsReset = ({
  selectedTabSectionKey,
  selectedDashboardTabSectionKey,
  rowDataReducerState,
  dispatch,
}: {
  selectedTabSectionKey: string | undefined;
  selectedDashboardTabSectionKey: string | undefined;
  rowDataReducerState: RowDataReducerState;
  dispatch: React.Dispatch<RowDataReducerAction>;
}): void => {
  const {
    rowData,
    resetState,
    deletedObjectsList: existingDeletedObjects,
  } = rowDataReducerState;

  const { newEntryRowData: resetNewEntryRowData, rowData: resetRowData } =
    resetState ?? {};

  const isClonedRecord =
    rowData[TRACK_RECORD_DATA_STATE_KEYS.TRACK_IS_CLONED_RECORD] === true;

  const isNewAndNotCloned = isNewRecord(rowData) && !isClonedRecord;

  let rowDataToUpdate: RowData = {};
  let updatedDeletedObjectsList = {};

  if (selectedTabSectionKey) {
    const nextSectionData = isNewAndNotCloned
      ? resetNewEntryRowData?.[selectedTabSectionKey]
      : resetRowData?.[selectedTabSectionKey];

    rowDataToUpdate = {
      ...rowData,
      [selectedTabSectionKey]: nextSectionData,
    } as RowData;

    updatedDeletedObjectsList = {
      ...existingDeletedObjects,
      [selectedTabSectionKey]: {},
    };
  } else if (selectedDashboardTabSectionKey) {
    const nextSectionData = isNewAndNotCloned
      ? resetNewEntryRowData?.[selectedDashboardTabSectionKey]
      : resetRowData?.[selectedDashboardTabSectionKey];

    rowDataToUpdate = nextSectionData as RowData;
  } else {
    if (isNewAndNotCloned) {
      rowDataToUpdate = resetNewEntryRowData as RowData;
    } else {
      rowDataToUpdate = resetRowData as RowData;
    }

    updatedDeletedObjectsList = {};
  }

  dispatch({
    type: "UPDATE_ROW_DATA_REDUCER_STATE",
    payload: {
      rowData: rowDataToUpdate,
      rowDataErrors: {},
      deletedObjectsList: updatedDeletedObjectsList,
    },
  });
};

export const isEqualTreatingNullAndEmptyAsEqual = (
  currentRowData: RowData,
  targetRowData: RowData,
): boolean => {
  const omitEmptyProperties = (rowData: RowData): RowData =>
    Object.fromEntries(
      Object.entries(rowData)
        .filter(([, value]) => value !== "" && value !== null)
        .map(([key, value]) => [
          key,
          Array.isArray(value)
            ? value.map((item) =>
                typeof item === "object" && item !== null
                  ? omitEmptyProperties(item as RowData)
                  : item,
              )
            : typeof value === "object"
              ? omitEmptyProperties(value as RowData)
              : value,
        ]),
    ) as RowData;

  return isEqualWith(
    omitEmptyProperties(currentRowData),
    omitEmptyProperties(targetRowData),
    (
      currentRowValue: RowDataValueType,
      targetRowValue: RowDataValueType,
    ): boolean | undefined => {
      if (
        (currentRowValue === "" && targetRowValue === null) ||
        (currentRowValue === null && targetRowValue === "")
      ) {
        return true; // treat "" and null as equal
      }
      return undefined;
    },
  );
};

export const getIsRecordChanged = ({
  selectedTabSectionKey,
  selectedDashboardTabSectionKey,
  rowDataReducerState,
}: {
  selectedTabSectionKey: string | undefined;
  selectedDashboardTabSectionKey: string | undefined;
  rowDataReducerState: RowDataReducerState;
}): boolean => {
  const { rowData, resetState, schema } = rowDataReducerState;
  const { newEntryRowData: resetNewEntryRowData, rowData: resetRowData } =
    resetState ?? {};

  const isClonedRecord =
    rowData?.[TRACK_RECORD_DATA_STATE_KEYS.TRACK_IS_CLONED_RECORD] === true;
  const isNewAndNotCloned = isNewRecord(rowData) && !isClonedRecord;

  let currentRowData = rowData as RowData | undefined;
  let compareResetNewEntryRowData = resetNewEntryRowData;
  let compareResetRowData = resetRowData;

  if (selectedTabSectionKey) {
    currentRowData = rowData?.[selectedTabSectionKey] as RowData | undefined;
    compareResetNewEntryRowData = resetNewEntryRowData?.[
      selectedTabSectionKey
    ] as RowData | undefined;
    compareResetRowData = resetRowData?.[selectedTabSectionKey] as
      | RowData
      | undefined;
  }

  if (selectedDashboardTabSectionKey) {
    compareResetNewEntryRowData = resetNewEntryRowData?.[
      selectedDashboardTabSectionKey
    ] as RowData | undefined;
    const isDashboardPrivib = isSetupRecordActionsRestricted(
      schema.title,
      selectedDashboardTabSectionKey,
    );
    compareResetRowData = isDashboardPrivib
      ? (resetRowData?.[selectedDashboardTabSectionKey] as RowData | undefined)
      : resetRowData;
  }

  const compareRowData = isNewAndNotCloned
    ? compareResetNewEntryRowData
    : compareResetRowData;

  if (!currentRowData) return false;

  return (
    !!compareRowData &&
    !isEqualTreatingNullAndEmptyAsEqual(currentRowData, compareRowData)
  );
};

export const clearRowDataErrorsForNonClonedRecords = (
  dispatch: React.Dispatch<RowDataReducerAction>,
  rowData: RowData = {},
): void => {
  const isClonedRecord = rowData[
    TRACK_RECORD_DATA_STATE_KEYS.TRACK_IS_CLONED_RECORD
  ] as boolean;

  if (!isClonedRecord) {
    dispatch({
      type: "UPDATE_ROW_DATA_REDUCER_STATE",
      payload: {
        rowDataErrors: {},
      },
    });
  }
};

export const getErrorFieldTitles = (
  rowDataErrors: RowDataErrors,
  schema: JsonSchema,
): string => {
  const titles: string[] = [];

  Object.keys(rowDataErrors).forEach((propertyKey) => {
    const property = schema.properties[propertyKey];
    if (property?.title) {
      titles.push(property.title);
    }
  });

  return titles.join(", ");
};
